import { DeleteOutlined, MoreOutlined, PlusOutlined } from "@ant-design/icons";
import { Button, Checkbox, CheckboxProps, Dropdown, Flex } from "antd";
import { FC, useCallback, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Point, Shelf, ShelvingItem, TaskShelfSize } from "tm-shared/types";

import { ROUTES } from "@/constants";
import { SVScanButton } from "@/features/SupervisorScan";
import { makeCommand, useAsyncAction, useMakeCommand } from "@/hooks";
import { useAppDispatch } from "@/hooks/redux";
import { ConfirmModal } from "@/pages/WorkPlan/Tasks/ShelfSize/components/ConfirmModal";
import { DeleteShelfModal } from "@/pages/WorkPlan/Tasks/ShelfSize/components/DeleteShelfModal";
import { Header } from "@/shared/layout";
import { MainContainer } from "@/shared/layout/Layout";
import BaseButton from "@/shared/ui/BaseButton";
import { PrimaryText, SecondaryText } from "@/shared/ui/Typography";
import { enqueueRequestQueueItem } from "@/store/slices/offline";
import { setTaskCompleted } from "@/store/slices/work";
import { handleFocus } from "@/utils/dom/handleFocus";

import {
    ButtonsWrapper,
    ShelfSizeBlock,
    StyledAddShelfButton,
    StyledDeleteButton,
    StyledDeleteMenuRow,
    StyledFormItem,
    StyledInputNumber,
    StyledInputString,
    StyledShelfRow,
} from "./Styled";

type ShelfWithIndex = Shelf & {
    index: number;
};

interface ShelvingItemWithIndex {
    name: string;
    index: number;
    shelfs: ShelfWithIndex[];
}

interface FocusTarget {
    shelvingIndex: number;
    subShelfIndex: number;
}

const menuItems = [
    {
        key: "1",
        label: (
            <StyledDeleteMenuRow>
                <DeleteOutlined /> Видалити стелаж
            </StyledDeleteMenuRow>
        ),
    },
];

const generateNumericId = (length = 10) => {
    return +Array.from({ length }, () => Math.floor(Math.random() * 10)).join(
        "",
    );
};

interface ShelfSizePageProps {
    point: Point;
    task: TaskShelfSize;
}

export const ShelfSizePage: FC<ShelfSizePageProps> = ({ point, task }) => {
    const navigate = useNavigate();
    const { makeTaskCompleteCommands } = useMakeCommand();
    const dispatch = useAppDispatch();

    const pointId = point.id;

    const [shelving, setShelving] = useState<ShelvingItemWithIndex[]>([]);
    const [emptyShelfs, setEmptyShelfs] = useState<boolean>(false);
    const [focusTarget, setFocusTarget] = useState<FocusTarget | null>(null);
    const [currentIndexForDelete, setCurrentIndexForDelete] = useState<
        number | null
    >(null);
    const [modal, setModal] = useState<"completeTask" | "deleteShelf" | null>(
        null,
    );

    const sanitizeShelvingData = (
        shelving: ShelvingItemWithIndex[],
    ): ShelvingItem[] => {
        return shelving.map(({ index: _index, shelfs, ...rest }) => ({
            ...rest,
            shelfs: shelfs.map(({ index: _index, ...shelfRest }) => shelfRest),
        }));
    };

    const taskId = task.id;

    const totalSize = useMemo(() => {
        if (emptyShelfs) {
            return 0;
        }
        return shelving.reduce((total, item) => {
            return (
                total + item.shelfs.reduce((sum, shelf) => sum + +shelf.size, 0)
            );
        }, 0);
    }, [shelving, emptyShelfs]);

    const handleCloseModal = useCallback(() => {
        setModal(null);
    }, []);

    const [handleCompleteTask, loading] = useAsyncAction(
        async (): Promise<void> => {
            const sanitizedData = sanitizeShelvingData(shelving);

            const shelvingData = {
                total: totalSize,
                shelving: emptyShelfs ? [] : sanitizedData,
            };

            const taskCompleteCommands = await makeTaskCompleteCommands({
                params: {
                    taskId,
                    hasPhoto: false,
                    payload: shelvingData,
                },
            });

            dispatch(
                enqueueRequestQueueItem({
                    id: taskId,
                    metadata: {
                        pointId,
                        taskId,
                    },
                    commands: [
                        makeCommand({
                            type: "patchMarketSetShelfSize",
                            title: "Оновлення розміру полиць",
                            params: { marketId: pointId, shelvingData },
                        }),

                        ...taskCompleteCommands,
                    ],
                }),
            );

            dispatch(setTaskCompleted({ targetId: pointId, taskId }));
            navigate(ROUTES.WorkPlan, { replace: true });
        },
    );

    const handleAddShelf = useCallback(() => {
        const newShelfIndex = shelving.length + 1;
        const uniqueId = generateNumericId();
        const newShelvingItem: ShelvingItemWithIndex = {
            name: `Стелаж_${newShelfIndex}`,
            index: uniqueId,
            shelfs: [
                {
                    name: `Полиця_${newShelfIndex}_1`,
                    size: 0,
                    index: 0,
                },
            ],
        };
        setShelving((prevState) => [...prevState, newShelvingItem]);
        setFocusTarget({ shelvingIndex: uniqueId, subShelfIndex: 0 });
    }, [shelving.length]);

    const handleAddSubShelf = useCallback((shelvingIndex: number) => {
        setShelving((prevState) =>
            prevState.map((item) => {
                if (item.index === shelvingIndex) {
                    const newSubShelfIndex = item.shelfs.length;
                    const uniqueId = generateNumericId();
                    const lastShelf = item.shelfs[item.shelfs.length - 1];

                    const newSubShelf: ShelfWithIndex = {
                        name: `Полиця_${prevState.length}_${
                            newSubShelfIndex + 1
                        }`,
                        size: lastShelf?.size ?? 0,
                        index: uniqueId,
                    };

                    setFocusTarget({
                        shelvingIndex,
                        subShelfIndex: uniqueId,
                    });

                    return {
                        ...item,
                        shelfs: [...item.shelfs, newSubShelf],
                    };
                }
                return item;
            }),
        );
    }, []);

    const handleChangeShelfName = useCallback(
        (shelvingIndex: number, value: string) => {
            setShelving((prevShelving) =>
                prevShelving.map((item) =>
                    item.index === shelvingIndex
                        ? { ...item, name: value }
                        : item,
                ),
            );
        },
        [],
    );

    const handleDeleteShelf = useCallback(() => {
        setShelving((prevState) =>
            prevState.filter(
                (shelfItem) => shelfItem.index !== currentIndexForDelete,
            ),
        );
        handleCloseModal();
    }, [currentIndexForDelete, handleCloseModal]);

    const handleMenuClick = useCallback(
        (eventKey: string, shelvingIndex: number) => {
            if (eventKey === "1") {
                setCurrentIndexForDelete(shelvingIndex);
                setModal("deleteShelf");
            }
        },
        [],
    );

    const handleDeleteSubShelf = useCallback(
        (shelvingIndex: number, subShelfIndex: number) => {
            setShelving((prevState) =>
                prevState.map((item) =>
                    item.index === shelvingIndex
                        ? {
                              ...item,
                              shelfs: item.shelfs.filter(
                                  (shelf) => shelf.index !== subShelfIndex,
                              ),
                          }
                        : item,
                ),
            );
        },
        [],
    );

    const editShelfFromShelving = useCallback(
        (
            shelvingIndex: number,
            subShelfIndex: number,
            updates: Partial<Omit<Shelf, "index">>,
        ): void => {
            setShelving((prevShelving) =>
                prevShelving.map((item) =>
                    item.index === shelvingIndex
                        ? {
                              ...item,
                              shelfs: item.shelfs.map((shelf) =>
                                  shelf.index === subShelfIndex
                                      ? { ...shelf, ...updates }
                                      : shelf,
                              ),
                          }
                        : item,
                ),
            );
        },
        [],
    );

    const onEmptyCheckboxChange = useCallback<
        NonNullable<CheckboxProps["onChange"]>
    >((e) => {
        setEmptyShelfs(e.target.checked);
    }, []);

    const isAllFieldsFilled = useMemo(() => {
        if (!shelving?.length) {
            return false;
        }
        return shelving.every((item) => {
            if (!item.name.trim()) return false;
            return item.shelfs.every(
                (shelf) => shelf.name.trim() && Number(shelf.size) > 0,
            );
        });
    }, [shelving]);

    const isLastShelfFilled = useCallback(
        (item: ShelvingItemWithIndex): boolean => {
            if (!item.shelfs || item.shelfs.length === 0) return true;

            const lastShelf = item.shelfs[item.shelfs.length - 1];

            return lastShelf?.size !== 0 && lastShelf?.size !== "";
        },
        [],
    );

    return (
        <>
            <Header
                title="Оновити інформацію про ТТ"
                back
                rightActions={
                    <SVScanButton pointId={pointId} taskId={taskId} />
                }
            />

            <MainContainer>
                <Flex vertical gap={16} style={{ marginBottom: 16 }}>
                    <PrimaryText fontSize={16}>{point?.name}</PrimaryText>

                    <PrimaryText>
                        Загальний поличний простір:{" "}
                        {shelving?.length
                            ? `${totalSize / 100}м ( ${totalSize}см )`
                            : !emptyShelfs
                              ? "--"
                              : totalSize}
                    </PrimaryText>
                </Flex>

                {shelving.map((item) => (
                    <ShelfSizeBlock key={item.index}>
                        <Flex
                            justify="space-between"
                            gap={16}
                            style={{ marginBottom: 8 }}
                        >
                            <StyledInputString
                                placeholder="Введіть назву стелажа"
                                value={item.name}
                                onChange={(e) =>
                                    handleChangeShelfName(
                                        item.index,
                                        e.target.value,
                                    )
                                }
                            />

                            {item.index > 0 && (
                                <Dropdown
                                    menu={{
                                        items: menuItems,
                                        onClick: (e) =>
                                            handleMenuClick(e.key, item.index),
                                    }}
                                >
                                    <MoreOutlined
                                        style={{
                                            fontSize: "20px",
                                        }}
                                    />
                                </Dropdown>
                            )}
                        </Flex>

                        <PrimaryText>Полиці</PrimaryText>

                        {item.shelfs.map((shelf) => {
                            const isNameEmpty = shelf.name.trim() === "";
                            const isSizeEmpty =
                                !shelf.size || +shelf.size === 0;

                            return (
                                <StyledShelfRow key={shelf.index}>
                                    <StyledFormItem
                                        validateStatus={
                                            isNameEmpty ? "error" : ""
                                        }
                                        help={
                                            isNameEmpty
                                                ? "Поле не може бути пустим"
                                                : ""
                                        }
                                    >
                                        <StyledInputString
                                            placeholder="Введіть назву полиці"
                                            onChange={(e) =>
                                                editShelfFromShelving(
                                                    item.index,
                                                    shelf.index,
                                                    {
                                                        name: e.target.value,
                                                    },
                                                )
                                            }
                                            value={shelf.name}
                                        />
                                    </StyledFormItem>

                                    <StyledFormItem
                                        validateStatus={
                                            isSizeEmpty && !isNameEmpty
                                                ? "error"
                                                : ""
                                        }
                                        help={
                                            isSizeEmpty && !isNameEmpty
                                                ? "Поле не може бути пустим"
                                                : ""
                                        }
                                    >
                                        <StyledInputNumber
                                            type="number"
                                            placeholder="см"
                                            min={0}
                                            precision={0}
                                            onFocus={handleFocus}
                                            disabled={isNameEmpty}
                                            value={shelf.size}
                                            ref={(input) => {
                                                if (
                                                    input &&
                                                    focusTarget?.shelvingIndex ===
                                                        item.index &&
                                                    focusTarget?.subShelfIndex ===
                                                        shelf.index
                                                ) {
                                                    input.focus();
                                                    setFocusTarget(null);
                                                }
                                            }}
                                            onChange={(value) =>
                                                editShelfFromShelving(
                                                    item.index,
                                                    shelf.index,
                                                    { size: value ?? "" },
                                                )
                                            }
                                            onKeyDown={(e) => {
                                                if (["."].includes(e.key)) {
                                                    e.preventDefault();
                                                }
                                            }}
                                        />
                                    </StyledFormItem>

                                    {item?.shelfs?.length > 1 && (
                                        <StyledDeleteButton
                                            type="text"
                                            onClick={() =>
                                                handleDeleteSubShelf(
                                                    item.index,
                                                    shelf.index,
                                                )
                                            }
                                            icon={<DeleteOutlined />}
                                        />
                                    )}
                                </StyledShelfRow>
                            );
                        })}

                        {item.name &&
                            item.shelfs.length < 8 &&
                            isLastShelfFilled(item) &&
                            isAllFieldsFilled && (
                                <Button
                                    type="text"
                                    onClick={() =>
                                        handleAddSubShelf(item.index)
                                    }
                                >
                                    <PrimaryText
                                        textColor="accent"
                                        fontSize={16}
                                        style={{
                                            textDecoration: "underline",
                                        }}
                                    >
                                        Додати полицю
                                    </PrimaryText>
                                </Button>
                            )}

                        {item.shelfs.length > 7 && (
                            <Flex justify="center" align="center" gap="8px">
                                <PrimaryText
                                    fontSize={16}
                                    style={{
                                        paddingTop: "16px",
                                    }}
                                >
                                    Максимально 8 полиць
                                </PrimaryText>
                            </Flex>
                        )}
                    </ShelfSizeBlock>
                ))}

                <ButtonsWrapper>
                    {!shelving?.length && (
                        <Checkbox
                            checked={emptyShelfs}
                            onChange={onEmptyCheckboxChange}
                        >
                            <SecondaryText fontSize={16}>
                                Відсутні полиці
                            </SecondaryText>
                        </Checkbox>
                    )}

                    <BaseButton
                        bgColor="success"
                        type="primary"
                        onClick={() => setModal("completeTask")}
                        disabled={
                            shelving?.length ? !isAllFieldsFilled : !emptyShelfs
                        }
                    >
                        Підтвердити
                    </BaseButton>
                </ButtonsWrapper>

                {((!emptyShelfs && !shelving?.length) || isAllFieldsFilled) && (
                    <StyledAddShelfButton
                        icon={<PlusOutlined />}
                        onClick={handleAddShelf}
                    />
                )}
            </MainContainer>

            <ConfirmModal
                open={modal === "completeTask"}
                onClose={handleCloseModal}
                onConfirm={handleCompleteTask}
                loading={loading}
            />

            <DeleteShelfModal
                open={modal === "deleteShelf"}
                onClose={handleCloseModal}
                onConfirm={handleDeleteShelf}
            />
        </>
    );
};
